import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  register: async () => {},
  requestEmailVerification: async () => {},
  verifyEmail: async () => {},
  requestPasswordReset: async () => {},
  resetPassword: async () => {},
  logout: async () => {},
  refreshAuth: async () => {}
});

function buildRequestError(payload, fallbackMessage) {
  const error = new Error(payload?.error || fallbackMessage);
  error.payload = payload;
  return error;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const response = await apiFetch("/api/auth/me");
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error("Could not load auth session.");
      }

      setUser(payload?.authenticated ? payload.user || null : null);
      return payload;
    } catch (error) {
      setUser(null);
      return { authenticated: false, user: null };
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async ({ identifier, password }) => {
    const response = await apiFetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ identifier, password })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not log in.");
    }

    setUser(payload.user || null);
    return payload;
  }, []);

  const register = useCallback(async ({ username, email, password, acceptedLegalTerms }) => {
    const response = await apiFetch("/api/auth/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ username, email, password, acceptedLegalTerms })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not register user.");
    }

    if (payload?.authenticated) {
      setUser(payload.user || null);
    }

    return payload;
  }, []);

  const requestEmailVerification = useCallback(async (identifier) => {
    const response = await apiFetch("/api/auth/email/verification/request", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ identifier })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not send verification email.");
    }

    return payload;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const response = await apiFetch("/api/auth/email/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ token })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not verify email.");
    }

    setUser(payload.user || null);
    return payload;
  }, []);

  const requestPasswordReset = useCallback(async (identifier) => {
    const response = await apiFetch("/api/auth/password/forgot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ identifier })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not create password reset request.");
    }

    return payload;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const response = await apiFetch("/api/auth/password/reset", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ token, password })
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw buildRequestError(payload, "Could not reset password.");
    }

    if (payload?.authenticated) {
      setUser(payload.user || null);
    }

    return payload;
  }, []);

  const logout = useCallback(async () => {
    const response = await apiFetch("/api/auth/logout", {
      method: "POST"
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || "Could not log out.");
    }

    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    register,
    requestEmailVerification,
    verifyEmail,
    requestPasswordReset,
    resetPassword,
    logout,
    refreshAuth
  }), [user, isLoading, login, register, requestEmailVerification, verifyEmail, requestPasswordReset, resetPassword, logout, refreshAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}